import React, { useState, useRef, useEffect } from 'react';
import { Upload, Link, Grid3X3, Search, X, Check, Loader2 } from 'lucide-react';
import './MemePickerModal.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

/**
 * MemePickerModal - Modal with 3 tabs for selecting memes:
 * 1. Upload - drag-and-drop or file picker
 * 2. Insert Link - external URL input
 * 3. Browse Library - searchable grid of library memes
 */
const MemePickerModal = ({
    isOpen,
    onClose,
    onSelect,
    memesLibrary = [],
    session,
    contextText = '',
    previousContext = ''
}) => {
    const [activeTab, setActiveTab] = useState('giphy');
    const [recommendedKeywords, setRecommendedKeywords] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [giphyQuery, setGiphyQuery] = useState('');
    const [giphyResults, setGiphyResults] = useState([]);
    const [giphyLoading, setGiphyLoading] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');
    const [linkPreview, setLinkPreview] = useState(null);
    const [linkError, setLinkError] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadPreview, setUploadPreview] = useState(null);
    const [selectedMeme, setSelectedMeme] = useState(null);
    const fileInputRef = useRef(null);

    // Filter memes by search query (Library tab)
    const filteredMemes = memesLibrary.filter(meme =>
        meme.label?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        meme.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    // Giphy Search
    const searchGiphy = async (query) => {
        if (!query) return;
        setGiphyLoading(true);
        try {
            const apiKey = process.env.REACT_APP_GIPHY_API_KEY;
            const response = await fetch(`https://api.giphy.com/v1/gifs/search?api_key=${apiKey}&q=${encodeURIComponent(query)}&limit=20&rating=g`);
            const data = await response.json();
            setGiphyResults(data.data || []);
        } catch (error) {
            console.error('Error searching Giphy:', error);
        } finally {
            setGiphyLoading(false);
        }
    };

    // Debounce Giphy search
    useEffect(() => {
        const timer = setTimeout(() => {
            if (activeTab === 'giphy' && giphyQuery) {
                searchGiphy(giphyQuery);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [giphyQuery, activeTab]);

    // AI Recommendation & State Reset
    useEffect(() => {
        if (isOpen) {
            // Logic when modal OPENS
            if (activeTab === 'giphy' && contextText && !giphyQuery) {
                const fetchRecommendations = async () => {
                    setGiphyLoading(true);
                    try {
                        const response = await fetch(`${API_BASE}/api/rant/recommend-meme`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${session?.access_token}`
                            },
                            body: JSON.stringify({
                                text: contextText,
                                context: previousContext
                            })
                        });
                        const data = await response.json();
                        if (data.keywords && data.keywords.length > 0) {
                            setRecommendedKeywords(data.keywords);
                            setGiphyQuery(data.keywords[0]); // Search the first one
                        }
                    } catch (error) {
                        console.error('AI Rec error:', error);
                    } finally {
                        setGiphyLoading(false);
                    }
                };
                fetchRecommendations();
            }
        } else {
            // Reset when modal CLOSES
            setGiphyQuery('');
            setGiphyResults([]);
            setRecommendedKeywords([]);
            setActiveTab('giphy');
        }
    }, [isOpen]);

    // Initial Trending Load (Modified to wait for AI check if context exists)
    useEffect(() => {
        // Only load trending if:
        // 1. We are on Giphy tab
        // 2. No query exists
        // 3. No results (prevents reloading if we just cleared query manually)
        // 4. We are NOT expecting AI recommendations (i.e. no contextText)
        if (activeTab === 'giphy' && !giphyQuery && giphyResults.length === 0 && !contextText) {
            const loadTrending = async () => {
                setGiphyLoading(true);
                try {
                    const apiKey = process.env.REACT_APP_GIPHY_API_KEY;
                    const response = await fetch(`https://api.giphy.com/v1/gifs/trending?api_key=${apiKey}&limit=20&rating=g`);
                    const data = await response.json();
                    setGiphyResults(data.data || []);
                } catch (error) {
                    console.error('Error loading trending Giphy:', error);
                } finally {
                    setGiphyLoading(false);
                }
            };
            loadTrending();
        }
    }, [activeTab, giphyQuery, contextText]);


    if (!isOpen) return null;

    // Handle file upload
    const handleFileSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        const validTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            alert('Please select a valid image file (PNG, JPG, GIF, or WebP)');
            return;
        }

        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
            setUploadPreview({
                file,
                dataUrl: e.target.result,
                name: file.name
            });
        };
        reader.readAsDataURL(file);
    };

    // Confirm upload
    const handleUploadConfirm = async () => {
        if (!uploadPreview?.file) return;

        setUploading(true);
        try {
            // For now, we'll use the data URL directly
            // In production, this would upload to Supabase storage
            onSelect({
                id: null,
                url: uploadPreview.dataUrl,
                source: 'upload'
            });
        } catch (err) {
            alert('Upload failed: ' + err.message);
        } finally {
            setUploading(false);
        }
    };

    // Validate link URL
    const handleLinkChange = (url) => {
        setLinkUrl(url);
        setLinkError('');
        setLinkPreview(null);

        if (!url) return;

        // Basic URL validation
        try {
            new URL(url);
            // Check if it looks like an image URL
            const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.webp'];
            const hasImageExt = imageExtensions.some(ext => url.toLowerCase().includes(ext));

            if (hasImageExt || url.includes('imgur') || url.includes('giphy')) {
                setLinkPreview(url);
            } else {
                // Try to load it as an image anyway
                setLinkPreview(url);
            }
        } catch {
            setLinkError('Please enter a valid URL');
        }
    };

    // Confirm link selection
    const handleLinkConfirm = () => {
        if (!linkPreview) return;
        onSelect({
            id: null,
            url: linkPreview,
            source: 'link'
        });
    };

    // Select from library
    const handleLibrarySelect = (meme) => {
        setSelectedMeme(meme);
    };

    // Confirm library selection
    const handleLibraryConfirm = () => {
        if (!selectedMeme) return;
        onSelect({
            id: selectedMeme.id,
            url: selectedMeme.url,
            source: 'library'
        });
    };

    // Select from Giphy
    const handleGiphySelect = (gif) => {
        onSelect({
            id: gif.id,
            url: gif.images.original.url,
            source: 'giphy'
        });
    };

    return (
        <div className="meme-picker-overlay" onClick={onClose}>
            <div className="meme-picker-modal" onClick={e => e.stopPropagation()}>
                <div className="meme-picker-header">
                    <h3>Select Meme</h3>
                    <button className="close-btn" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                {/* Tabs */}
                <div className="meme-picker-tabs">
                    <button
                        className={`picker-tab ${activeTab === 'giphy' ? 'active' : ''}`}
                        onClick={() => setActiveTab('giphy')}
                    >
                        <Search size={16} /> Giphy
                    </button>
                    <button
                        className={`picker-tab ${activeTab === 'upload' ? 'active' : ''}`}
                        onClick={() => setActiveTab('upload')}
                    >
                        <Upload size={16} /> Upload
                    </button>
                    <button
                        className={`picker-tab ${activeTab === 'link' ? 'active' : ''}`}
                        onClick={() => setActiveTab('link')}
                    >
                        <Link size={16} /> Insert Link
                    </button>
                    <button
                        className={`picker-tab ${activeTab === 'library' ? 'active' : ''}`}
                        onClick={() => setActiveTab('library')}
                    >
                        <Grid3X3 size={16} /> Browse Library
                    </button>
                </div>

                {/* Tab Content */}
                <div className="meme-picker-content">
                    {/* Giphy Tab */}
                    {activeTab === 'giphy' && (
                        <div className="library-tab">
                            <div className="search-row">
                                <Search size={16} />
                                <input
                                    type="text"
                                    placeholder="Search Giphy..."
                                    value={giphyQuery}
                                    onChange={(e) => setGiphyQuery(e.target.value)}
                                    autoFocus
                                />
                            </div>

                            {/* Recommendation Chips */}
                            {recommendedKeywords.length > 0 && (
                                <div className="recommendation-chips" style={{ display: 'flex', gap: '8px', padding: '0 16px 12px', overflowX: 'auto', whiteSpace: 'nowrap' }}>
                                    <span style={{ fontSize: '12px', color: '#a3a3a3', display: 'flex', alignItems: 'center' }}>✨ AI Suggested:</span>
                                    {recommendedKeywords.map((kw, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setGiphyQuery(kw)}
                                            style={{
                                                background: giphyQuery === kw ? 'rgba(6, 182, 212, 0.2)' : 'rgba(255,255,255,0.05)',
                                                border: giphyQuery === kw ? '1px solid #06b6d4' : '1px solid rgba(255,255,255,0.1)',
                                                borderRadius: '16px',
                                                padding: '4px 12px',
                                                color: giphyQuery === kw ? '#06b6d4' : '#e5e5e5',
                                                fontSize: '12px',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            {kw}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <div className="meme-grid">
                                {giphyLoading ? (
                                    <div className="loading-container" style={{ display: 'flex', width: '100%', justifyContent: 'center', padding: '2rem' }}>
                                        <Loader2 size={32} className="spin" />
                                    </div>
                                ) : giphyResults.length === 0 ? (
                                    <p className="no-memes">No results found</p>
                                ) : (
                                    giphyResults.map(gif => (
                                        <div
                                            key={gif.id}
                                            className="meme-item"
                                            onClick={() => handleGiphySelect(gif)}
                                        >
                                            <img
                                                src={gif.images.fixed_height_small.url}
                                                alt={gif.title}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                        </div>
                                    ))
                                )}
                            </div>
                            <div className="giphy-attribution" style={{ textAlign: 'right', padding: '5px 10px' }}>
                                <img src="/giphy-logo.png" alt="Powered by GIPHY" style={{ height: '15px', opacity: 0.7 }} onError={(e) => e.target.style.display = 'none'} />
                                <span style={{ fontSize: '10px', color: '#666' }}>Powered by GIPHY</span>
                            </div>
                        </div>
                    )}

                    {/* Upload Tab */}
                    {activeTab === 'upload' && (
                        <div className="upload-tab">
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileSelect}
                                accept=".png,.jpg,.jpeg,.gif,.webp"
                                style={{ display: 'none' }}
                            />

                            {!uploadPreview ? (
                                <div
                                    className="upload-dropzone"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <Upload size={48} />
                                    <p>Click to upload or drag and drop</p>
                                    <span>PNG, JPG, GIF, WebP</span>
                                </div>
                            ) : (
                                <div className="upload-preview">
                                    <img src={uploadPreview.dataUrl} alt="Preview" />
                                    <p>{uploadPreview.name}</p>
                                    <div className="preview-actions">
                                        <button
                                            className="cancel-btn"
                                            onClick={() => setUploadPreview(null)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className="confirm-btn"
                                            onClick={handleUploadConfirm}
                                            disabled={uploading}
                                        >
                                            {uploading ? <Loader2 size={16} className="spin" /> : <Check size={16} />}
                                            Use This
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Insert Link Tab */}
                    {activeTab === 'link' && (
                        <div className="link-tab">
                            <div className="link-input-row">
                                <input
                                    type="text"
                                    placeholder="Paste image URL..."
                                    value={linkUrl}
                                    onChange={(e) => handleLinkChange(e.target.value)}
                                    className={linkError ? 'error' : ''}
                                />
                            </div>
                            {linkError && <p className="error-text">{linkError}</p>}

                            {linkPreview && (
                                <div className="link-preview">
                                    <img
                                        src={linkPreview}
                                        alt="Preview"
                                        onError={() => {
                                            setLinkError('Could not load image');
                                            setLinkPreview(null);
                                        }}
                                    />
                                    <button
                                        className="confirm-btn"
                                        onClick={handleLinkConfirm}
                                    >
                                        <Check size={16} /> Use This
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Browse Library Tab */}
                    {activeTab === 'library' && (
                        <div className="library-tab">
                            <div className="search-row">
                                <Search size={16} />
                                <input
                                    type="text"
                                    placeholder="Search memes..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>

                            <div className="meme-grid">
                                {filteredMemes.length === 0 ? (
                                    <p className="no-memes">
                                        {memesLibrary.length === 0
                                            ? 'No memes in library'
                                            : 'No memes match your search'}
                                    </p>
                                ) : (
                                    filteredMemes.map(meme => (
                                        <div
                                            key={meme.id}
                                            className={`meme-item ${selectedMeme?.id === meme.id ? 'selected' : ''}`}
                                            onClick={() => handleLibrarySelect(meme)}
                                        >
                                            <img src={meme.url} alt={meme.label} />
                                            <span>{meme.label}</span>
                                        </div>
                                    ))
                                )}
                            </div>

                            {selectedMeme && (
                                <div className="library-footer">
                                    <div className="selected-preview">
                                        <img src={selectedMeme.url} alt={selectedMeme.label} />
                                        <span>{selectedMeme.label}</span>
                                    </div>
                                    <button
                                        className="confirm-btn"
                                        onClick={handleLibraryConfirm}
                                    >
                                        <Check size={16} /> Select
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MemePickerModal;
